import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as dashboardActions from "src/redux/slices/dashboardSlice";
import TableV2 from "src/components/ui/TableV2";
import useWarehouseLabel from "src/hooks/useWarehouseLabel";

interface LowStockCardProps {
  limit?: number;
  enabled?: boolean;
  canReorder?: boolean;
  warehouseId?: string;
}

const LowStockCard: React.FC<LowStockCardProps> = ({
  limit = 5,
  enabled = true,
  canReorder = false,
  warehouseId,
}) => {
  const dispatch = useAppDispatch();
  const { tenantLowStockAlerts } = useAppSelector((state) => state.dashboard);
  const { singular: warehouseLabel } = useWarehouseLabel();

  useEffect(() => {
    if (!enabled) return;
    dispatch(
      dashboardActions.getTenantLowStockAlertsRequest({
        limit,
        warehouseId: warehouseId || undefined,
      })
    );
  }, [dispatch, enabled, limit, warehouseId]);

  if (!enabled) return null;

  const rows = tenantLowStockAlerts.data?.products || [];
  const columns = [
    {
      key: "productId",
      header: "Product ID",
      accessor: ({ row }: { row: any }) => (
        <span className="text-gray-600">{row.productId || "-"}</span>
      ),
    },
    {
      key: "name",
      header: "Product",
      accessor: ({ row }: { row: any }) => (
        <span className="font-medium text-gray-900 dark:text-gray-100">{row.name}</span>
      ),
    },
    {
      key: "warehouseName",
      header: warehouseLabel,
      accessor: ({ row }: { row: any }) => (
        <span className="text-gray-600 dark:text-gray-400">
          {row.warehouseName || "-"}
        </span>
      ),
    },
    { key: "stock", header: "Current Stock" },
    { key: "minStock", header: "Reorder Level" },
    {
      key: "action",
      header: "Action",
      accessor: () =>
        canReorder ? (
          <Link
            to="/app/purchase/list"
            className="rounded-md border border-primary-200 px-3 py-1 text-xs font-semibold text-primary-600 hover:border-primary-300"
          >
            Reorder
          </Link>
        ) : (
          <span className="text-xs text-gray-400 dark:text-gray-500">-</span>
        ),
    },
  ];

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Low Stock Alerts</h2>
      <div className="mt-4">
        <TableV2 columns={columns} data={rows} loading={tenantLowStockAlerts.loading} />
      </div>
    </div>
  );
};

export default LowStockCard;
